import FormattedMessage from 'I18n/components/FormattedMessage';
import { useEffect, useState } from 'react';
import UIBox from 'UIComponents/layout/UIBox';
// @ts-expect-error dependency missing types
import UpgradeProductToApiNameMap from 'self-service-api/constants/UpgradeProductToApiNameMap';
import { getUpgradeButtonText, getUpgradeButtonTextFromApiName } from 'ui-addon-upgrades/_core/common/adapters/getUpgradeButtonText';
import { USAGE_LIMIT_BANNER } from 'ui-addon-upgrades/_core/common/constants/PurchaseMotionLocations';
import { ASSIGN_SEAT, PRICING_PAGE_REDIRECT, REQUEST_SEAT } from 'ui-addon-upgrades/_core/purchaseMotions/PurchaseMotionTypes';
import UIPurchaseMotionButton from 'ui-addon-upgrades/button/UIPurchaseMotionButton';
import UIPricingPageRedirectButton from '../../button/UIPricingPageRedirectButton';
import { STARTER } from 'self-service-api/constants/ProductLevels';
import { CORE, SALES, SERVICE } from 'self-service-api/constants/UnitScopes';
import { getProductNameTextFromApiName } from '../../_core/common/adapters/getProductNameText';
import displayAddOnLimitIncreaseCopy from '../displayAddOnLimitIncreaseCopy';
import getPrioritizedUpgradeProduct, { getPrioritizedUpgradeProductFromApiName } from '../getPrioritizedUpgradeProduct';
import { getBannerI18nSubTitleKey, getBannerI18nTitleKey, getHubAndTierFromApiName } from '../utils';
import DefaultBanner from './DefaultBanner';
import { useUpgradeDataFromUpgradeConfig } from './useUpgradeDataFromUpgradConfig';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const getSeatType = apiName => {
  const {
    hub,
    tier
  } = getHubAndTierFromApiName(apiName);
  if (tier === STARTER.toLocaleLowerCase()) {
    return `${CORE}_STARTER`;
  }
  if (hub === SALES.toLocaleLowerCase()) {
    return `${SALES}_${tier.toLocaleUpperCase()}`;
  } else if (hub === SERVICE.toLocaleLowerCase()) {
    return `${SERVICE}_${tier.toLocaleUpperCase()}`;
  }
  return `${CORE}_${tier.toLocaleUpperCase()}`;
};
export const UsageLimitBannerTitle = ({
  featureTranslationKey,
  value,
  limit
}) => {
  return /*#__PURE__*/_jsx(FormattedMessage, {
    message: getBannerI18nTitleKey('upgrades.usageLimitBanner.header', featureTranslationKey),
    options: {
      value,
      limit
    }
  });
};
export const UsageLimitBannerSubtext = ({
  apiName,
  featureTranslationKey,
  purchaseMotions
}) => {
  const translatedProductName = getProductNameTextFromApiName(apiName);
  const addOnCopyOverride = displayAddOnLimitIncreaseCopy(apiName);
  const seatsCopyOverride = purchaseMotions ? purchaseMotions.filter(pm => pm === ASSIGN_SEAT || pm === REQUEST_SEAT).length > 0 : false;
  if (seatsCopyOverride) {
    return /*#__PURE__*/_jsxs(UIBox, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext.seats`, featureTranslationKey)
      }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
        message: `upgrades.seats.${getSeatType(apiName)}`
      }), "."]
    });
  }
  if (addOnCopyOverride) {
    return /*#__PURE__*/_jsxs(UIBox, {
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
      }), "\xA0", /*#__PURE__*/_jsx(FormattedMessage, {
        message: `upgrades.usageLimitBanner.addOnLimitIncreaseOverride`,
        options: {
          productName: translatedProductName
        }
      })]
    });
  }
  return /*#__PURE__*/_jsxs(UIBox, {
    children: [/*#__PURE__*/_jsx(FormattedMessage, {
      message: getBannerI18nSubTitleKey(`upgrades.usageLimitBanner.subtext`, featureTranslationKey)
    }), "\xA0", translatedProductName && /*#__PURE__*/_jsx(FormattedMessage, {
      message: translatedProductName ? `upgrades.message.productName` : '',
      options: {
        productName: translatedProductName
      }
    })]
  });
};
const UsageLimitBanner = ({
  upgradePoint,
  limit,
  value,
  displayThreshold,
  hasReachedLimit,
  className,
  style,
  'data-test-id': dataTestId = 'usage-limit-banner',
  purchaseMotionData,
  ownedProducts,
  isFreePortal = false
}) => {
  const [purchaseMotions, setPurchaseMotions] = useState([]);
  const upgradeData = useUpgradeDataFromUpgradeConfig(upgradePoint);
  const purchaseMotionsFromData = purchaseMotionData && purchaseMotionData.locations && purchaseMotionData.locations.USAGE_LIMIT_BANNER && purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions ? purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions : null;
  const buildApiNamesToUse = () => {
    if (upgradeData.apiNames) {
      return upgradeData.apiNames;
    } else if (upgradeData.upgradeProducts) {
      return upgradeData.upgradeProducts.map(product => UpgradeProductToApiNameMap[product]);
    }
    return [];
  };
  const apiNamesToUse = buildApiNamesToUse();

  //if there is no purchase motion data fallback to default upgrade product
  upgradeData.upgradeProduct = purchaseMotionsFromData ? getPrioritizedUpgradeProduct(upgradePoint.upgradeProducts, purchaseMotionsFromData, ownedProducts, isFreePortal) : upgradePoint.upgradeProducts[0];
  if (purchaseMotionsFromData) {
    const recommendedHub = upgradePoint.recommendationResponse && upgradePoint.recommendationResponse.hubType;
    upgradeData.apiName = getPrioritizedUpgradeProductFromApiName(apiNamesToUse, purchaseMotionsFromData, ownedProducts, isFreePortal, recommendedHub);
  }
  useEffect(() => {
    const fallbackPurchaseMotions = [PRICING_PAGE_REDIRECT];
    if (!upgradeData.apiName || !purchaseMotionData || !purchaseMotionData.locations || !purchaseMotionData.locations.USAGE_LIMIT_BANNER || !purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions) {
      setPurchaseMotions(fallbackPurchaseMotions);
    } else {
      // already have purchase motions from props so transform those instead of fetching
      const usageLimitBannerPMs = purchaseMotionData.locations.USAGE_LIMIT_BANNER.purchaseMotions;
      const rawPurchaseMotions = usageLimitBannerPMs[upgradeData.apiName];
      if (rawPurchaseMotions) {
        const transformedPurchaseMotions = Object.values(rawPurchaseMotions).flat().map(obj => obj ? obj.key : obj);
        setPurchaseMotions(transformedPurchaseMotions);
      } else {
        setPurchaseMotions(fallbackPurchaseMotions);
      }
    }
  }, [upgradeData.upgradeProduct, purchaseMotionData, upgradeData.apiName]);
  const usageLimitBannerData = {
    limit,
    value,
    displayThreshold,
    hasReachedLimit
  };
  const pricingPageRedirectButton = () => /*#__PURE__*/_jsx(UIPricingPageRedirectButton, {
    upgradeData: upgradeData,
    use: "tertiary-light",
    level: 1,
    isUpgradePoint: true,
    "data-test-id": "upgrade-button",
    usageLimitBannerData: usageLimitBannerData,
    goToUpgradePage: true,
    children: upgradeData.apiName ? getUpgradeButtonTextFromApiName('default', upgradeData.apiName) : getUpgradeButtonText('default', upgradeData.upgradeProduct)
  });
  const getUpgradeButton = () => {
    return /*#__PURE__*/_jsx(UIPurchaseMotionButton, {
      upgradeData: upgradeData,
      usageLimitBannerData: usageLimitBannerData,
      location: USAGE_LIMIT_BANNER,
      level: 1,
      use: "tertiary-light",
      purchaseMotions: purchaseMotions,
      "data-test-id": "upgrade-button",
      overrides: {
        //Adding this override because of the text changes in the CTA button for pricing page redirect
        [PRICING_PAGE_REDIRECT]: pricingPageRedirectButton
      }
    });
  };
  if (displayThreshold && value < displayThreshold) return null;
  return /*#__PURE__*/_jsx(DefaultBanner, {
    titleText: /*#__PURE__*/_jsx(UsageLimitBannerTitle, {
      featureTranslationKey: upgradePoint.featureTranslationKey,
      limit: limit,
      value: value
    }),
    className: className,
    dataTestId: dataTestId,
    subtext: /*#__PURE__*/_jsx(UsageLimitBannerSubtext, {
      apiName: upgradeData.apiName || UpgradeProductToApiNameMap[upgradeData.upgradeProduct],
      featureTranslationKey: upgradePoint.featureTranslationKey,
      purchaseMotions: purchaseMotions
    }),
    style: style,
    button: getUpgradeButton()
  });
};
export default UsageLimitBanner;